import { fileRequestSchema, type FilesConnection } from '@atum-remote/protocol/files';
import { linuxEventSchema, type PrivilegeConnection } from '@atum-remote/protocol/linux-delegation';
import { z } from 'zod';
import { browserTabProtocols } from './lib/browser-tab.js';
import { bindSessionActivity } from './lib/session-activity.js';
import {
  serverInspectionRequest,
  type ServerInspectionConnection,
} from '@atum-remote/protocol/server-inspection';
import { deviceActionSchema } from '@atum-remote/protocol/device-actions';

export async function browserDeviceActionConnection(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
) {
  return browserDataConnection(
    apiBase,
    lease,
    closeLease,
    deviceActionSchema,
    'device_action',
    30000,
  );
}

export async function browserInspectionConnection(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
): Promise<ServerInspectionConnection> {
  return browserDataConnection(
    apiBase,
    lease,
    closeLease,
    serverInspectionRequest,
    'inspection',
    // Two 3-second authority checks surround the owner's 14-second RPC budget.
    25000,
  );
}

export async function browserFilesConnection(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
): Promise<FilesConnection> {
  return browserDataConnection(apiBase, lease, closeLease, fileRequestSchema, 'files', 10000);
}
export async function browserPrivilegeConnection(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
): Promise<PrivilegeConnection> {
  return browserDataConnection(apiBase, lease, closeLease, linuxEventSchema, 'privilege', 24000);
}
async function browserDataConnection<T>(
  apiBase: string,
  lease: unknown,
  closeLease: (id: string) => Promise<unknown>,
  schema: z.ZodType<T>,
  kind: 'files' | 'privilege' | 'inspection' | 'device_action',
  timeout: number,
) {
  const session = z
    .object({
      sessionId: z.uuid(),
      key: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
      expiresAt: z.number().int(),
    })
    .parse(lease);
  const url = new URL(`${apiBase}/browser/sessions/${session.sessionId}/socket`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const socket = new WebSocket(url, browserTabProtocols());
  let ready = false;
  const unbindActivity = bindSessionActivity(socket, () => ready);
  let pending:
    | {
        id: string;
        resolve(value: Record<string, unknown>): void;
        reject(error: Error): void;
        timer: ReturnType<typeof setTimeout>;
      }
    | undefined;
  let resolveReady: () => void = () => undefined,
    rejectReady: (error: Error) => void = () => undefined;
  const opened = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  const fail = () => {
    unbindActivity();
    ready = false;
    const error = new Error(
      kind === 'inspection'
        ? 'Server connection ended. Reconnect to take a fresh reading. The Linux host must support server inspection.'
        : kind === 'files'
          ? 'File connection ended. Inspect the folder before retrying an upload.'
          : kind === 'device_action'
            ? 'Device connection ended. Check the device before making another request.'
            : 'Privilege connection ended. Inspect the request receipt; uncertain operations are never retried automatically.',
    );
    rejectReady(error);
    if (pending) {
      clearTimeout(pending.timer);
      pending.reject(error);
      pending = undefined;
    }
  };
  const startup = setTimeout(() => {
    fail();
    socket.close();
  }, 12000);
  socket.onopen = () => socket.send(JSON.stringify({ type: 'attach', key: session.key }));
  socket.onerror = socket.onclose = fail;
  socket.onmessage = (message) => {
    try {
      if (typeof message.data !== 'string' || new TextEncoder().encode(message.data).length > 65536)
        throw new Error('Invalid frame.');
      const value: unknown = JSON.parse(message.data);
      const activity = z
        .object({ type: z.literal('session_activity'), expiresAt: z.number().int().positive() })
        .strict()
        .safeParse(value);
      if (ready && activity.success) {
        session.expiresAt = activity.data.expiresAt;
        return;
      }
      if (!ready) {
        z.object({ type: z.literal('ready'), expiresAt: z.number() }).parse(value);
        ready = true;
        clearTimeout(startup);
        resolveReady();
        return;
      }
      const response = z
        .object({
          type: z.literal(`${kind}_result`),
          id: z.uuid(),
          result: z.record(z.string(), z.unknown()),
        })
        .strict()
        .parse(value);
      if (!pending || response.id !== pending.id) throw new Error('Unexpected response.');
      const request = pending;
      pending = undefined;
      clearTimeout(request.timer);
      request.resolve(response.result);
    } catch {
      fail();
      socket.close();
    }
  };
  try {
    await opened;
  } catch (error) {
    clearTimeout(startup);
    socket.close();
    void closeLease(session.sessionId).catch(() => undefined);
    throw error;
  }
  return {
    isOpen: () => ready && socket.readyState === WebSocket.OPEN && Date.now() < session.expiresAt,
    async request(input: T): Promise<Record<string, unknown>> {
      const request = schema.parse(input);
      if (
        !ready ||
        pending ||
        socket.readyState !== WebSocket.OPEN ||
        Date.now() >= session.expiresAt
      )
        throw new Error(
          kind === 'inspection'
            ? 'Reconnect to inspect this server.'
            : kind === 'files'
              ? 'Reconnect to access files.'
              : kind === 'device_action'
                ? 'Reconnect to use device actions.'
                : 'Reconnect to service controls.',
        );
      return new Promise((resolve, reject) => {
        const id = crypto.randomUUID();
        pending = {
          id,
          resolve,
          reject,
          timer: setTimeout(() => {
            fail();
            socket.close();
          }, timeout),
        };
        try {
          socket.send(JSON.stringify({ type: kind, id, request }));
        } catch {
          fail();
          socket.close();
        }
      });
    },
    async close() {
      clearTimeout(startup);
      fail();
      socket.close();
      await closeLease(session.sessionId).catch(() => undefined);
    },
  };
}
